import React from "react";
import { useNavigate } from "react-router-dom";
import { Bike, Coins, Gift, Megaphone, ScrollText, Medal, Award, Trophy, Users } from "lucide-react";
import { SectionHeader } from "./Rates";
import { SCHEME_BENEFITS, MONTHLY_PRIZES } from "../mock";
import { useAuth } from "../context/AuthContext";

const ICONS = { Bike, Coins, Gift, Megaphone, ScrollText, Medal, Award };

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const RewardScheme = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const joinScheme = () => {
    if (user) navigate("/account");
    else login("/account");
  };

  return (
    <section id="scheme" className="relative py-24 bg-[#0b0f19]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="Golden Reward Scheme" title="Unveiling the Ultimate Rewards!" sub="Subscribe once. Win every month for 40 months. Gold, silver, scooter & more." />

        <div className="flex flex-col items-center mb-14">
          <p className="font-ui text-[11px] tracking-[0.25em] uppercase text-gray-400">Join Now — One Time Payment Only</p>
          <div className="font-display gold-gradient-text text-6xl md:text-7xl mt-2">₹65,000/-</div>
          <p className="font-body italic text-gray-500 mt-2">What you will get if you subscribe ↓</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SCHEME_BENEFITS.map((b) => {
            const Icon = ICONS[b.icon];
            return (
              <div key={b.n} className="relative border border-white/8 bg-white/[0.02] p-8 card-glow overflow-hidden">
                <span className="absolute top-4 right-6 font-display text-7xl text-gold/10">{b.n}</span>
                <div className="h-14 w-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold">
                  <Icon size={26} />
                </div>
                <h3 className="font-display text-2xl text-white mt-6">{b.title}</h3>
                <p className="font-body text-gray-400 mt-3 leading-relaxed">{b.desc}</p>
                <span className="inline-block mt-5 bg-gold/10 text-gold font-ui text-[10px] tracking-wider uppercase px-3 py-1.5">{b.tag}</span>
              </div>
            );
          })}
          <div className="border border-gold/25 bg-gradient-to-br from-gold/[0.08] to-transparent p-8 flex flex-col justify-center items-center text-center">
            <Trophy className="text-gold" size={40} />
            <h3 className="font-display text-2xl text-white mt-4">Ready to Win?</h3>
            <p className="font-body text-gray-400 mt-2">Join 200 members growing wealth together.</p>
            <button onClick={joinScheme} className="gold-btn font-ui text-xs tracking-[0.2em] uppercase px-7 py-3.5 mt-5 font-medium">Join The Scheme</button>
          </div>
        </div>

        {/* Every month prizes */}
        <h3 className="font-display smallcaps text-3xl text-white text-center mt-20 mb-8">Every Month Prizes</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {MONTHLY_PRIZES.map((p, i) => {
            const Icon = ICONS[p.icon] || Coins;
            return (
              <div key={i} className="border border-white/8 bg-white/[0.02] p-6 text-center card-glow">
                <Icon className="text-gold mx-auto" size={30} />
                <h4 className="font-display text-xl text-white mt-4">{p.title}</h4>
                <p className="font-body text-gray-400 text-sm mt-1">{p.sub}</p>
                <span className="inline-block mt-4 bg-white/8 text-gray-300 font-ui text-[9px] tracking-wider uppercase px-3 py-1">{p.tag}</span>
              </div>
            );
          })}
        </div>

        {/* Milestone + maturity */}
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div className="border border-gold/20 bg-gold/[0.04] p-8">
            <h3 className="font-display text-2xl text-white flex items-center gap-3"><Bike className="text-gold" size={26} /> Bajaj Chetak Electric Scooter</h3>
            <p className="font-body text-gray-400 mt-3">Exclusive for One-Time ₹65,000 members. Four scooters to be won over the scheme duration.</p>
            <span className="inline-block mt-4 bg-gold/10 text-gold font-ui text-[10px] tracking-wider uppercase px-3 py-1.5">Prize on: Month 10, 20, 30 & 40</span>
          </div>
          <div className="border border-white/10 bg-white/[0.02] p-8">
            <h3 className="font-display text-2xl text-white flex items-center gap-3"><Coins className="text-gold" size={26} /> 5 Gram Gold Coin</h3>
            <p className="font-body text-gray-400 mt-3">Special milestone prize — four gold coins across the scheme.</p>
            <span className="inline-block mt-4 bg-gold/10 text-gold font-ui text-[10px] tracking-wider uppercase px-3 py-1.5">Prize on: Month 9, 18, 27 & 36</span>
          </div>
        </div>

        <div className="border border-white/10 bg-white/[0.02] p-8 mt-6 text-center">
          <p className="font-ui text-[11px] tracking-[0.25em] uppercase text-gold flex items-center justify-center gap-2"><Users size={16} /> Total: 200 Members · 40 Months</p>
          <p className="font-body text-gray-400 mt-3 max-w-2xl mx-auto">32 winners every month. A community of investors growing their wealth together through precious metals.</p>
        </div>

        <div className="border border-gold/25 bg-gradient-to-br from-gold/[0.07] to-transparent p-10 mt-6">
          <h3 className="font-display smallcaps text-3xl text-white text-center flex items-center justify-center gap-3"><Trophy className="text-gold" size={28} /> Post 40-Month Maturity Benefit</h3>
          <p className="font-body italic text-gray-400 mt-2 text-center">After 40 months, the choice is completely yours. The power is in your hands!</p>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="border border-white/10 bg-black/20 p-6">
              <span className="font-display text-3xl text-gold">①</span>
              <p className="font-body text-gray-200 mt-2">Purchase ANY Gold or Silver product from TwinBrain Jewellery with your complete accumulated maturity value.</p>
            </div>
            <div className="border border-white/10 bg-black/20 p-6">
              <span className="font-display text-3xl text-gold">②</span>
              <p className="font-body text-gray-200 mt-2">Receive your complete money back — 100% of your investment returned.</p>
            </div>
          </div>
          <p className="font-display smallcaps text-xl text-gold text-center mt-8 tracking-[0.15em]">The Power Is In Your Hands! ✦</p>
        </div>
      </div>
    </section>
  );
};

export default RewardScheme;
